<template>
  <div class="card card-plain">
    <div v-if="alert.message" class="card-header py-2">
      <argon-alert
        v-for="msg in alert.message" :key="msg"
        class="text-sm text-center" 
        :color="alert.type">
        {{msg}}
      </argon-alert>
    </div>
    <div class="card-body pt-0">
      <form @submit.prevent="WithDraw">
        <div class="d-flex flex-column justify-content-center align-items-center">
          <div class="mb-4 d-flex flex-column align-items-center gap-3 text-center">
            <fa-icon class="text-primary" icon="fa-solid fa-shield-halved" size="6x" />
            <h6>Na telefonní číslo <span class="text-primary">{{ userPhone }}</span> jsem Ti odeslala autorizační kód pro výběr prostředků</h6>
          </div>
          <div class="otp-input-wrapper">
            <v-otp-input
              ref="otpSmsInput"
              :value="SMSCode"
              input-classes="otp-input sms-code-input"
              separator="-"
              :num-inputs="6"
              :should-auto-focus="true"
              :is-input-num="true"
              @on-change="handleSMSCode"
              @on-complete="handleSMSCode"
            />
            <span v-if="v$.SMSCode.$error" class="text-danger text-sm">
              {{ v$.SMSCode.$errors[0].$message }}
            </span>
          </div>
          <div class="sms-auth-countdown-wrapper mt-4">
            <sms-auth-countdown
              tag="div"
              class="text mt-2"
              :time="10 * 60 * 1000"
              v-slot="{ minutes, seconds }"
              @end="onSMSAuthCountdownEnd"
            >
              <p class="text-center">Platnost autorizačního kódu vyprší za <span class="text-primary">{{ minutes }}min {{ seconds }}s</span>.</p> 
            </sms-auth-countdown>
          </div>
        </div>
        <div class="d-flex flex-column">
          <argon-button
            class="my-4"
            type="submit" 
            color="primary"
            size="lg"
            :disabled="isButtonLoading"
            fullWidth>
            <span v-if="isButtonLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Potvrdit
          </argon-button>
        </div>
      </form> 
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, minLength } from '@vuelidate/validators';
import VOtpInput from 'vue3-otp-input';
import SmsAuthCountdown from '@chenfengyuan/vue-countdown';
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonAlert from "@/components/ArgonAlert.vue";
import dataService from '@/services/dataService';

const props = defineProps({
  userPhone: String
});
const emit = defineEmits(['withdraw-success']);

const swal = inject('$swal');
const store = useStore();

const otpSmsInput = ref(null);
const SMSCode = ref(null);
    
const alert = computed(() => store.state.alert);
const isButtonLoading = computed(() => store.getters['loadingModule/isButtonLoading']);
const rules = computed(() => {
  return {
    SMSCode: { 
      required: helpers.withMessage('Zadej ověřovací kód z SMS!', required),
      minLength: helpers.withMessage('Zadej ověřovací kód z SMS!', minLength(6))
    },
  }
})
const v$ = useVuelidate(rules, { SMSCode });

const resetForm = () => {
  v$.value.$reset();
  otpSmsInput.value.clearInput();
}

const WithDraw = async () => {
  const isFormCorrect = await v$.value.$validate();
  
  if (!isFormCorrect) return;

  try {
    store.dispatch('loadingModule/setButtonLoading', true);
    const response = await dataService.WithDraw(store.state.user.UserID, store.state.token, SMSCode.value);
    
    if (response.Result) {
      emit('withdraw-success');
      store.dispatch('closeModal');

      // Show SweetAlert2
      swal.fire({
        position: 'center',
        icon: 'success',
        html: `${response.InfoMessage.map(message => `<h5>${message}</h5>`).join('')}`,
        showConfirmButton: false,
        timer: 3000,
      })
    } else {
      resetForm();
      store.dispatch('alertError', response.ErrorMessage);
    }
  } catch (error) {
    console.error(error);
  } finally {
    store.dispatch('loadingModule/setButtonLoading', false);
  }
}

const handleSMSCode = (value) => { SMSCode.value = value };

const onSMSAuthCountdownEnd = () => {
  store.dispatch('closeModal');
  swal.fire({
    position: 'center',
    icon: 'error',
    html: '<h5>Platnost autorizačního kódu vypršela</h5>',
    showConfirmButton: false,
    showCloseButton: true,
  })
};

</script>